/* Automation Centre · Founder OS v1 · Batch P2.2 · Feb 2026
 * ==========================================================
 * Replaces the "Settings" mindset. Each capability has three modes:
 * Manual · Assisted · Autonomous. Feature owners consult the
 * canonical preference before taking any autonomous action.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Cog, User, Zap, Sparkles } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL || "";

const MODE_META = {
  manual:     { label: "Manual",     desc: "You drive every step. NDG never acts on its own.",       icon: User    },
  assisted:   { label: "Assisted",   desc: "NDG suggests. You approve.",                              icon: Sparkles },
  autonomous: { label: "Autonomous", desc: "NDG handles routine steps and keeps you informed.",      icon: Zap     },
};

const CAPABILITY_LABEL = {
  day_planning:       "Plan my day",
  lesson_prep:        "Prepare each lesson",
  reflection_prompts: "Reflection prompts",
  business_admin:     "Business admin",
};

export default function AutomationCentre() {
  const [prefs, setPrefs] = useState(null);
  const [state, setState] = useState("loading");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/api/automation-preferences`, { withCredentials: true });
        setPrefs(r.data);
        setState("ok");
      } catch {
        setState("error");
      }
    })();
  }, []);

  const setMode = async (capability, mode) => {
    if (!prefs || saving) return;
    setSaving(true);
    const nextModes = { ...(prefs.modes || {}), [capability]: mode };
    try {
      const r = await axios.put(
        `${API}/api/automation-preferences`,
        { modes: nextModes },
        { withCredentials: true },
      );
      setPrefs(r.data);
      toast.success("Automation updated");
    } catch {
      toast.error("Couldn't save automation preference");
    } finally {
      setSaving(false);
    }
  };

  if (state === "loading") {
    return <div data-testid="automation-loading" className="min-h-dvh grid place-items-center bg-slate-50 dark:bg-slate-950"><p className="text-sm text-slate-500">Loading…</p></div>;
  }
  if (state !== "ok" || !prefs) {
    return <div data-testid="automation-error" className="min-h-dvh grid place-items-center bg-slate-50 dark:bg-slate-950"><p className="text-sm text-rose-600">Couldn&apos;t load Automation Centre.</p></div>;
  }

  return (
    <div data-testid="automation-centre" className="min-h-dvh bg-slate-50 dark:bg-slate-950 p-4">
      <div className="max-w-xl mx-auto space-y-4">
        <header className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-lg bg-slate-900 text-white grid place-items-center">
            <Cog className="w-4 h-4" />
          </div>
          <div>
            <h1 className="text-lg font-extrabold text-slate-900 dark:text-white">Automation Centre</h1>
            <p className="text-xs text-slate-500">Choose how much NDG handles for you.</p>
          </div>
        </header>

        <ul className="space-y-3" data-testid="automation-list">
          {(prefs.capabilities || []).map((cap) => {
            const current = (prefs.modes || {})[cap] || "manual";
            return (
              <li key={cap} data-testid={`automation-row-${cap}`} className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4">
                <p className="text-sm font-bold text-slate-900 dark:text-white mb-2">
                  {CAPABILITY_LABEL[cap] || cap}
                </p>
                <div className="grid grid-cols-3 gap-2">
                  {(prefs.canonical_modes || []).map((mode) => {
                    const active = mode === current;
                    const Icon = MODE_META[mode]?.icon || User;
                    return (
                      <button
                        key={mode}
                        data-testid={`automation-${cap}-${mode}`}
                        onClick={() => setMode(cap, mode)}
                        disabled={saving}
                        className={`p-2 rounded-lg text-[11px] font-semibold flex flex-col items-center gap-0.5 border transition-colors ${active ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 dark:border-slate-800 text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800"}`}
                      >
                        <Icon className="w-3.5 h-3.5" />
                        {MODE_META[mode]?.label || mode}
                      </button>
                    );
                  })}
                </div>
                <p className="text-[11px] text-slate-500 mt-2">
                  {MODE_META[current]?.desc}
                </p>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}
